import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

type TrashKey = "products" | "enquiries" | "testimonials" | "contact_details";

interface TrashItem {
  id: string;
  deletedAt: string | null;
  title: string;
  subtitle: string;
}

const TABS: { key: TrashKey; label: string; icon: string }[] = [
  { key: "products", label: "Products", icon: "ri-stack-line" },
  { key: "enquiries", label: "Enquiries", icon: "ri-inbox-line" },
  { key: "testimonials", label: "Testimonials", icon: "ri-chat-quote-line" },
  { key: "contact_details", label: "Contact Details", icon: "ri-contacts-line" },
];

const EMPTY_COUNTS: Record<TrashKey, number> = {
  products: 0,
  enquiries: 0,
  testimonials: 0,
  contact_details: 0,
};

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function mapRow(key: TrashKey, row: Record<string, unknown>): TrashItem {
  const id = String(row.id);
  const deletedAt = asString(row.deleted_at) || null;
  switch (key) {
    case "products":
      return {
        id,
        deletedAt,
        title: asString(row.name) || "Untitled product",
        subtitle: [asString(row.category), asString(row.make), asString(row.model)]
          .filter(Boolean)
          .join(" · "),
      };
    case "enquiries":
      return {
        id,
        deletedAt,
        title: asString(row.name) || asString(row.email) || "Enquiry",
        subtitle: [asString(row.subject), asString(row.product_name)].filter(Boolean).join(" · "),
      };
    case "testimonials":
      return {
        id,
        deletedAt,
        title: asString(row.name) || "Review",
        subtitle: asString(row.content).slice(0, 90),
      };
    case "contact_details":
    default:
      return {
        id,
        deletedAt,
        title: asString(row.value) || "Contact detail",
        subtitle: [asString(row.type), asString(row.label)].filter(Boolean).join(" · "),
      };
  }
}

export default function TrashManager() {
  const [tab, setTab] = useState<TrashKey>("products");
  const [items, setItems] = useState<TrashItem[]>([]);
  const [counts, setCounts] = useState<Record<TrashKey, number>>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const [confirmPurge, setConfirmPurge] = useState<TrashItem | null>(null);

  const loadCounts = async () => {
    const entries = await Promise.all(
      TABS.map(async (t) => {
        const { count } = await supabase
          .from(t.key)
          .select("id", { count: "exact", head: true })
          .not("deleted_at", "is", null);
        return [t.key, count ?? 0] as const;
      })
    );
    setCounts(Object.fromEntries(entries) as Record<TrashKey, number>);
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from(tab)
        .select("*")
        .not("deleted_at", "is", null)
        .order("deleted_at", { ascending: false });
      if (err) throw err;
      setItems((data || []).map((row) => mapRow(tab, row as Record<string, unknown>)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load trash");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const restore = async (item: TrashItem) => {
    setBusyId(item.id);
    setError("");
    const { error: err } = await supabase
      .from(tab)
      .update({ deleted_at: null })
      .eq("id", item.id);
    setBusyId("");
    if (err) {
      setError(err.message);
      return;
    }
    setNotice(`"${item.title}" restored.`);
    load();
    loadCounts();
  };

  const purge = async () => {
    if (!confirmPurge) return;
    const target = confirmPurge;
    setBusyId(target.id);
    setError("");
    const { error: err } = await supabase.from(tab).delete().eq("id", target.id);
    setBusyId("");
    setConfirmPurge(null);
    if (err) {
      setError(err.message);
      return;
    }
    setNotice(`"${target.title}" permanently deleted.`);
    load();
    loadCounts();
  };

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—";

  const activeTab = TABS.find((t) => t.key === tab);

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">Trash</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Deleted items land here first — restore anything, or delete it forever.
          </p>
        </div>
      </div>

      <div className="mt-5 inline-flex flex-wrap gap-1 rounded-full border border-neutral-200 bg-white p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition whitespace-nowrap ${
              tab === t.key
                ? "bg-brand-dark text-brand-gold"
                : "text-neutral-500 hover:bg-neutral-100"
            }`}
          >
            <i className={t.icon}></i>
            {t.label}
            {counts[t.key] > 0 && (
              <span
                className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                  tab === t.key ? "bg-brand-gold text-brand-dark" : "bg-neutral-200 text-neutral-600"
                }`}
              >
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError("")} className="text-red-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
      {notice && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-4 py-3">
          <span className="text-sm text-green-700">{notice}</span>
          <button onClick={() => setNotice("")} className="text-green-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold"></i>
        </div>
      ) : items.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-neutral-300 bg-white py-16 text-center">
          <i className="ri-delete-bin-6-line text-4xl text-neutral-300"></i>
          <p className="mt-3 text-sm text-neutral-400">
            Trash is empty for {activeTab?.label.toLowerCase()}.
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">Deleted</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-brand-dark">{item.title}</p>
                    {item.subtitle && (
                      <p className="text-xs text-neutral-500">{item.subtitle}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{formatDate(item.deletedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => restore(item)}
                        disabled={busyId === item.id}
                        className="flex items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 disabled:opacity-60 whitespace-nowrap"
                        title="Restore"
                      >
                        <i className="ri-arrow-go-back-line"></i>
                        Restore
                      </button>
                      <button
                        onClick={() => setConfirmPurge(item)}
                        disabled={busyId === item.id}
                        className="flex items-center gap-2 rounded-md border border-red-200 px-3 py-2 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-60 whitespace-nowrap"
                        title="Delete forever"
                      >
                        <i className="ri-delete-bin-line"></i>
                        Delete Forever
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmPurge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
              <i className="ri-delete-bin-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-brand-dark">
              Delete forever?
            </h3>
            <p className="mt-2 text-sm text-neutral-500">
              &quot;{confirmPurge.title}&quot; will be permanently removed. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setConfirmPurge(null)}
                className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={purge}
                className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}